import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { normalizeRecordContent } from '@/utils/companyRecords';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const ENDPOINT = `${STRAPI_URL}/api/company-records`;
const PAGE_SIZE = 100;
const POPULATE = {
	'populate[subject][fields][0]': 'name',
	'populate[subject][fields][1]': 'documentId',
};

export const normalizeCompanyRecord = (entry = {}) => {
	const source = entry?.attributes ? { ...entry.attributes, id: entry.id } : entry || {};
	const subject = source.subject?.data ?? source.subject ?? null;
	const templateKey = source.templateKey || '';
	return {
		id: source.id ?? null,
		documentId: source.documentId ?? null,
		templateKey,
		title: source.title || '',
		reference: source.reference || '',
		version: Number(source.version) || 1,
		revision: Number(source.revision) || 0,
		reviewedOn: source.reviewedOn || '',
		nextReviewOn: source.nextReviewOn || '',
		subject: subject
			? { id: subject.id ?? null, documentId: subject.documentId ?? null, name: subject.name || '' }
			: null,
		subjectName: source.subjectName || '',
		content: normalizeRecordContent(source.content, templateKey),
		createdAt: source.createdAt ?? null,
		updatedAt: source.updatedAt ?? null,
	};
};

/** The reason code the server gives when it refuses a change (archived, changed elsewhere…). */
export const companyRecordErrorCode = (error) => error?.response?.data?.error?.details?.code || null;

// Before the server knows company records the route is missing (404) or not yet allowed (403).
const isUnavailable = (error) => [403, 404].includes(error?.response?.status);

const sortRecords = (items) =>
	[...items].sort(
		(a, b) =>
			a.templateKey.localeCompare(b.templateKey) ||
			String(a.subject?.name || '').localeCompare(String(b.subject?.name || '')) ||
			b.version - a.version,
	);

export const useCompanyRecordsStore = defineStore('companyRecords', {
	state: () => ({
		items: [],
		isLoading: false,
		hasFetched: false,
		unavailable: false,
		error: null,
		// Counts changes made here; a list read that was out while one happened is older than it.
		changeCount: 0,
	}),
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		// Reads every version without touching the list, so a page can decide whether it is safe
		// to show what changed on another device.
		async peekRecords() {
			const items = [];
			let page = 1;
			let pageCount = 1;
			do {
				const response = await axios.get(ENDPOINT, {
					headers: this.authHeaders(),
					params: {
						'pagination[page]': page,
						'pagination[pageSize]': PAGE_SIZE,
						'sort[0]': 'templateKey:asc',
						'sort[1]': 'version:desc',
						...POPULATE,
					},
				});
				const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
				items.push(...rows.map(normalizeCompanyRecord));
				pageCount = Number(response?.data?.meta?.pagination?.pageCount) || 1;
				page += 1;
			} while (page <= pageCount);
			return sortRecords(items);
		},

		applyRecords(items, { since = this.changeCount } = {}) {
			if (since !== this.changeCount) return false;
			this.items = items;
			this.hasFetched = true;
			this.unavailable = false;
			this.error = null;
			return true;
		},

		async fetchRecords({ force = false } = {}) {
			if (this.isLoading && !force) return this.items;
			this.isLoading = true;
			const since = this.changeCount;
			try {
				const items = await this.peekRecords();
				if (!this.applyRecords(items, { since })) this.hasFetched = true;
				return this.items;
			} catch (error) {
				if (isUnavailable(error)) {
					this.items = [];
					this.hasFetched = true;
					this.unavailable = true;
					return this.items;
				}
				this.error = error?.response?.data?.error?.message || error?.message || 'Failed to load company records';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},

		async peekRecord(documentId) {
			const response = await axios.get(`${ENDPOINT}/${documentId}`, {
				headers: this.authHeaders(),
				params: POPULATE,
			});
			return response?.data?.data ? normalizeCompanyRecord(response.data.data) : null;
		},

		upsert(record) {
			if (!record?.documentId) return record;
			const others = this.items.filter((item) => item.documentId !== record.documentId);
			this.items = sortRecords([...others, record]);
			this.changeCount += 1;
			return record;
		},

		async fetchRecord(documentId) {
			if (!documentId) return null;
			const record = await this.peekRecord(documentId);
			return record ? this.upsert(record) : null;
		},

		async createRecord(payload) {
			const response = await axios.post(
				ENDPOINT,
				{ data: payload },
				{ headers: this.authHeaders(), params: POPULATE },
			);
			return this.upsert(normalizeCompanyRecord(response?.data?.data));
		},

		/** Saves changes made on top of `revision`; the server refuses it if the record moved on. */
		async updateRecord(documentId, changes) {
			const response = await axios.put(
				`${ENDPOINT}/${documentId}`,
				{ data: changes },
				{ headers: this.authHeaders(), params: POPULATE },
			);
			return this.upsert(normalizeCompanyRecord(response?.data?.data));
		},

		async deleteRecord(documentId) {
			await axios.delete(`${ENDPOINT}/${documentId}`, { headers: this.authHeaders() });
			this.items = this.items.filter((item) => item.documentId !== documentId);
			this.changeCount += 1;
		},
	},
});
